import { useState, useCallback, useRef } from "react";
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from "lucide-react";

/**
 * useToast
 * Drop-in replacement for window.alert().
 * Usage:
 *   const { toast, showToast, hideToast } = useToast();
 *   showToast("Saved successfully", "success");
 *   showToast(err.response?.data?.message || "Something went wrong", "error");
 *
 * Then render <Toast toast={toast} onClose={hideToast} /> once, near the
 * top of the component's returned JSX.
 */
export function useToast(defaultDuration = 4000) {
  const [toast, setToast] = useState(null); // { message, type }
  const timerRef = useRef(null);

  const hideToast = useCallback(() => {
    setToast(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const showToast = useCallback((message, type = "success", duration = defaultDuration) => {
    if (!message) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setToast({ message, type });
    timerRef.current = setTimeout(() => {
      setToast(null);
      timerRef.current = null;
    }, duration);
  }, [defaultDuration]);

  return { toast, showToast, hideToast };
}

const VARIANTS = {
  success: {
    icon: CheckCircle2,
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    text: "text-emerald-700",
    iconColor: "text-emerald-500",
  },
  error: {
    icon: XCircle,
    bg: "bg-red-50",
    border: "border-red-200",
    text: "text-red-700",
    iconColor: "text-red-500",
  },
  warning: {
    icon: AlertTriangle,
    bg: "bg-amber-50",
    border: "border-amber-200",
    text: "text-amber-700",
    iconColor: "text-amber-500",
  },
  info: {
    icon: Info,
    bg: "bg-blue-50",
    border: "border-blue-200",
    text: "text-blue-700",
    iconColor: "text-blue-500",
  },
};

/**
 * Toast - fixed banner pinned to the top-center of the viewport.
 * Render this once per page, alongside the useToast() hook.
 */
export default function Toast({ toast, onClose }) {
  if (!toast) return null;
  const variant = VARIANTS[toast.type] || VARIANTS.success;
  const Icon = variant.icon;

  return (
    <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[999] w-full max-w-md px-4 animate-in fade-in slide-in-from-top-2 duration-200">
      <div
        role="alert"
        className={`flex items-start gap-3 ${variant.bg} ${variant.border} ${variant.text} border rounded-xl shadow-lg px-4 py-3`}
      >
        <Icon size={18} className={`${variant.iconColor} mt-0.5 shrink-0`} />
        <p className="text-sm font-bold flex-1 leading-snug">{toast.message}</p>
        <button
          onClick={onClose}
          className="shrink-0 opacity-60 hover:opacity-100 transition"
          aria-label="Dismiss"
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}